// integrations/alm/ajax-load-more-manager.js

/**
 * Ajax Load More ↔ Barba.js glue.
 *
 * ALM is built for full page loads, so SPA navigation needs help at three points:
 *   - leave:  tear ALM down so its global scroll handler can't rewrite the URL
 *             back to the archive mid-transition (teardownAjaxLoadMore).
 *   - enter:  re-execute the incoming page's per-instance config scripts, which
 *             live in the footer and are never swapped by Barba (syncAlmConfigScripts).
 *   - enter:  re-initialise the listings + filters in the new container
 *             (reinitializeAjaxLoadMore).
 *
 * Masonry archives are handled separately in alm-masonry.js (ALM's own masonry
 * throws on SPA re-init).
 */

const NOOP_LOGGER = { log() {}, warn() {} };

/**
 * Create a namespaced logger that is silent unless debugging is enabled.
 * @param {boolean} debug - Whether to emit log output.
 * @return {{log: function, warn: function}} Logger with `log` and `warn`.
 */
function createLogger(debug) {
  if (!debug) return NOOP_LOGGER;
  return {
    log: (...args) => console.log('[ALM]', ...args),
    warn: (...args) => console.warn('[ALM]', ...args),
  };
}

/**
 * Run a function with history.pushState/replaceState temporarily suppressed,
 * then restore the original methods.
 *
 * ALM Filters' `reset()` fires its internal TriggerChange, which — for a
 * change-style filter (e.g. the news archive) — pushStates the URL back to the
 * filter base (the archive permalink). During a Barba leave this lands AFTER
 * Barba has already pushed the incoming article URL, so the archive URL wins
 * and the address bar reverts to /news/ even though the article renders.
 * Button-style filters (e.g. assets) don't auto-sync the URL on change, which
 * is why they never showed the bug. Suppressing history writes for the reset
 * neutralises the revert without affecting the fresh almfilters.start() on
 * enter. The captured references are whatever is currently installed (incl.
 * the Barba history-proxy patch), so restoration preserves that patch.
 *
 * @param {function} fn - The function to run with history writes suppressed.
 * @return {void}
 */
function withHistorySuppressed(fn) {
  const push = history.pushState;
  const replace = history.replaceState;
  history.pushState = function () {};
  history.replaceState = function () {};
  try {
    fn();
  } finally {
    history.pushState = push;
    history.replaceState = replace;
  }
}

/**
 * Reset ALM and ALM Filters runtime state (if present).
 * @param {object} logger - Namespaced logger.
 * @return {void}
 */
function resetAlmState(logger) {
  if (window.ajaxloadmore && typeof window.ajaxloadmore.reset === 'function') {
    logger.log('reset ajaxloadmore.');
    window.ajaxloadmore.reset();
  }
  if (window.almfilters && typeof window.almfilters.reset === 'function') {
    logger.log('reset almfilters.');
    // See withHistorySuppressed: almfilters.reset() would otherwise pushState
    // the archive URL over Barba's freshly-pushed article URL.
    withHistorySuppressed(() => window.almfilters.reset());
  }
}

/**
 * Collect the ALM wraps within a container (including the container itself).
 * @param {Element} container - Scope to search.
 * @return {Element[]} The `.ajax-load-more-wrap` elements.
 */
function collectAlmWraps(container) {
  if (!container) return [];
  const wraps = Array.from(container.querySelectorAll('.ajax-load-more-wrap'));
  if (container.classList && container.classList.contains('ajax-load-more-wrap')) {
    wraps.push(container);
  }
  return wraps;
}

/**
 * Tear down Ajax Load More before a Barba transition leaves the current page.
 *
 * ALM's filters bind a global scroll handler that rewrites the URL via
 * history.replaceState to the archive's permalink. Its internal guard bails only
 * when the listing is absent from the DOM or hidden (`offsetParent === null`) —
 * but during Barba's leave the outgoing container is `position: fixed` (not yet
 * `display: none`) for a moment, so a scroll fired mid-transition (e.g. the enter
 * hook's scrollTo(0)) passes the guard and reverts the URL to the archive after
 * Barba already pushed the new one. Removing the ALM wraps from the leaving
 * container up front makes the guard bail immediately.
 *
 * @param {Element} container - The outgoing Barba container.
 * @param {boolean} debug - Whether to log debugging information.
 * @return {void}
 */
export function teardownAjaxLoadMore(container, debug = false) {
  const logger = createLogger(debug);
  resetAlmState(logger);

  const wraps = collectAlmWraps(container);
  wraps.forEach((wrap) => wrap.remove());
  if (wraps.length) {
    logger.log(`removed ${wraps.length} wrap(s) from the leaving container to stop stale scroll/history handlers.`);
  }
}

/**
 * Re-execute the incoming page's Ajax Load More per-instance config scripts.
 *
 * ALM prints each instance's config as `<script id="ajax_load_more_{id}_vars">`
 * in the footer — OUTSIDE the Barba container — so it is never swapped or
 * re-executed on SPA navigation. Without it, `window.ajax_load_more_{id}_vars` is
 * missing/stale after a transition and re-init mis-queries (renders "No posts
 * found"). This copies the incoming page's config script(s) into the live
 * document so the globals are correct before re-initialising ALM.
 *
 * @param {Document} nextDoc - The parsed next-page document (full page, incl. footer).
 * @param {boolean} debug - Whether to log debugging information.
 * @return {void}
 */
export function syncAlmConfigScripts(nextDoc, debug = false) {
  if (!nextDoc) return;
  const logger = createLogger(debug);

  nextDoc.querySelectorAll('script[id^="ajax_load_more_"]').forEach((incoming) => {
    const existing = document.getElementById(incoming.id);
    if (existing) existing.remove();

    const script = document.createElement('script');
    Array.from(incoming.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
    script.textContent = incoming.textContent;
    document.body.appendChild(script);

    logger.log('re-executed config script', incoming.id);
  });
}

/**
 * Re-initialise the ALM listings in a freshly-entered container.
 *
 * Instances whose `data-id` is duplicated on the page are skipped: ALM localises
 * each instance's config into a single global keyed by id, so shared-id instances
 * (e.g. the theme's shared 'ajax_status' on the two team listings) collide, and
 * re-initialising them would query with the wrong (last-wins) config and wipe the
 * content. Their server-rendered (preloaded) markup is already correct, so they
 * are left untouched. Single-instance archives (news, awards, assets) have a
 * unique id per page and are re-initialised normally.
 *
 * @param {Element[]} wraps - Candidate ALM wraps.
 * @param {object} logger - Namespaced logger.
 * @return {void}
 */
function reinitListings(wraps, logger) {
  const idCounts = {};
  wraps.forEach((el) => {
    const id = el.getAttribute('data-id') || '';
    idCounts[id] = (idCounts[id] || 0) + 1;
  });

  const unique = wraps.filter((el) => idCounts[el.getAttribute('data-id') || ''] === 1);
  const collided = wraps.filter((el) => idCounts[el.getAttribute('data-id') || ''] > 1);
  if (collided.length) {
    logger.log(`skipped ${collided.length} wrap(s) with a duplicate data-id (kept server-rendered).`);
  }

  if (!unique.length) {
    logger.log('no re-initialisable listings.');
    return;
  }

  // Prefer window.almInit() — it honours the preloaded/pause state (no spurious
  // extra page load) unlike ajaxloadmore.start(), which forces an initial fetch.
  if (typeof window.almInit === 'function') {
    logger.log(`re-init ${unique.length} listing(s) via almInit.`);
    unique.forEach((el) => window.almInit(el));
  } else if (window.ajaxloadmore && typeof window.ajaxloadmore.start === 'function') {
    logger.log(`re-init ${unique.length} listing(s) via ajaxloadmore.start.`);
    unique.forEach((el) => window.ajaxloadmore.start(el));
  } else {
    logger.warn('no ALM init function available (almInit / ajaxloadmore.start).');
  }
}

/**
 * Re-initialise ALM Filters if any filter UI is present.
 * Gated on filter-UI presence (not listing count) so filters still initialise on
 * pages whose only ALM instances are preloaded.
 * @param {Element} container - The entered container.
 * @param {object} logger - Namespaced logger.
 * @return {void}
 */
function reinitFilters(container, logger) {
  if (!window.almfilters || typeof window.almfilters.start !== 'function') return;

  const hasFilters = !!((container && container.querySelector('.alm-filters-container')) || document.querySelector('.alm-filters-container'));
  if (hasFilters) {
    logger.log('restart almfilters.');
    window.almfilters.start();
  } else {
    logger.log('no filter UI, skipping almfilters.start().');
  }
}

/**
 * Re-initialise ALM listings and filters within a freshly-entered container.
 * @param {Element} container - The entered Barba container.
 * @param {boolean} debug - Whether to log debugging information.
 * @return {void}
 */
export function reinitializeAjaxLoadMore(container, debug = false) {
  const logger = createLogger(debug);
  reinitListings(collectAlmWraps(container), logger);
  reinitFilters(container, logger);
}
